<template>
  <section id="docs-detail">
    <b-modal
      id="confirm-reject"
      ref="confirmRejectModal"
      title="Rejeter cette proposition ?"
      ok-title="Rejeter"
      ok-variant="danger"
      cancel-title="Annuler"
      cancel-variant="outline-secondary"
      centered
      no-fade
      @ok="reject()"
    >
      La proposition sera supprimée du repo <code>buxida-docs</code>. Cette
      action ne peut pas être annulée (mais l'article publié, s'il existe,
      n'est pas affecté).
    </b-modal>

    <b-card>
      <div class="d-flex justify-content-between align-items-start mb-2">
        <div>
          <b-button variant="link" class="p-0 mb-1" @click="$router.push({ name: 'docs' })">
            <feather-icon icon="ArrowLeftIcon" /> Retour à la liste
          </b-button>
          <h4 class="mb-0">
            {{ metadata.title || slug }}
            <b-badge v-if="isNewArticle" variant="light-info" class="ml-1">Nouvel article</b-badge>
          </h4>
          <p v-if="proposalSource" class="text-muted small mb-0 mt-1">
            Proposée par <code>{{ proposalSource }}</code>
            <span v-if="proposalCreatedAt"> · {{ formatDate(proposalCreatedAt) }}</span>
          </p>
          <p v-if="proposalSummary" class="text-muted mb-0 mt-1">
            <strong>Résumé :</strong> {{ proposalSummary }}
          </p>
        </div>
        <div class="d-flex flex-wrap" style="gap: 8px">
          <b-button
            v-if="isNewArticle"
            v-ripple.400="'rgba(255, 255, 255, 0.15)'"
            variant="outline-info"
            :disabled="loading"
            :title="'Copie un prompt prêt à coller dans une session Claude Code web bi-repo (buxapi + buxida-docs)'"
            @click="copyDraftPrompt()"
          >
            <feather-icon icon="CopyIcon" /> Copier le prompt rédaction
          </b-button>
          <b-button
            v-ripple.400="'rgba(255, 255, 255, 0.15)'"
            variant="outline-secondary"
            :disabled="loading || saving || publishing || rejecting || !dirty"
            @click="save()"
          >
            <b-spinner v-if="saving" small />
            <feather-icon v-else icon="SaveIcon" /> Sauvegarder
          </b-button>
          <b-button
            v-ripple.400="'rgba(255, 255, 255, 0.15)'"
            variant="success"
            :disabled="loading || saving || publishing || rejecting"
            @click="publish()"
          >
            <b-spinner v-if="publishing" small />
            <feather-icon v-else icon="UploadCloudIcon" /> Publier
          </b-button>
          <b-button
            v-ripple.400="'rgba(255, 255, 255, 0.15)'"
            variant="outline-danger"
            :disabled="loading || saving || publishing || rejecting"
            @click="confirmReject()"
          >
            <b-spinner v-if="rejecting" small />
            <feather-icon v-else icon="XCircleIcon" /> Rejeter
          </b-button>
        </div>
      </div>

      <div v-if="loading" class="text-center my-3">
        <b-spinner variant="primary" /> Chargement…
      </div>

      <template v-else>
        <metadata-panel
          v-model="metadata"
          :suggestions="relatedReposSuggestions"
          :categories="categories"
        />

        <div class="d-flex align-items-center mt-2 mb-1" style="gap: 8px">
          <b-button
            v-ripple.400="'rgba(0, 0, 0, 0.08)'"
            size="sm"
            variant="outline-secondary"
            :title="showPublished ? 'Masquer la version publiée pour libérer la moitié de l\'écran' : 'Réafficher la version publiée'"
            @click="togglePublished"
          >
            <feather-icon :icon="showPublished ? 'ChevronLeftIcon' : 'ChevronRightIcon'" />
            {{ showPublished ? 'Masquer la version publiée' : 'Afficher la version publiée' }}
          </b-button>
          <b-button
            v-ripple.400="'rgba(0, 0, 0, 0.08)'"
            size="sm"
            variant="outline-secondary"
            :title="editorMode === 'wysiwyg' ? 'Repasser à l\'éditeur Markdown classique' : 'Basculer en éditeur WYSIWYG (Toast UI, Markdown-natif)'"
            @click="toggleEditorMode"
          >
            <feather-icon icon="EyeIcon" />
            Éditeur : {{ editorMode === 'wysiwyg' ? 'WYSIWYG' : 'Markdown' }}
          </b-button>
        </div>
        <b-row>
          <b-col v-if="showPublished" md="6">
            <h6 class="text-muted">Version publiée</h6>
            <published-preview :content="publishedBody" />
          </b-col>
          <b-col :md="showPublished ? 6 : 12">
            <h6 class="text-muted">
              Proposition <span v-if="dirty" class="text-warning">(édité)</span>
            </h6>
            <wysiwyg-editor
              v-if="editorMode === 'wysiwyg'"
              v-model="body"
              height="60vh"
              @library-error="onLibraryError"
            />
            <markdown-editor
              v-else
              v-model="body"
              height="60vh"
              @library-error="onLibraryError"
            />
          </b-col>
        </b-row>

        <!-- Panneau audit Azure (V2-P3) — collapse, charge a la demande -->
        <b-card no-body class="mt-2 azure-panel">
          <b-card-header
            class="py-1 d-flex justify-content-between align-items-center"
            style="cursor: pointer"
            @click="toggleAzurePanel"
          >
            <h5 class="mb-0">
              <feather-icon icon="ImageIcon" /> Images Azure
              <b-badge
                v-if="azureImages.loaded"
                :variant="azureMissingCount > 0 ? 'light-warning' : 'light-success'"
                class="ml-2"
              >
                {{ azureSummary }}
              </b-badge>
            </h5>
            <feather-icon :icon="showAzurePanel ? 'ChevronUpIcon' : 'ChevronDownIcon'" />
          </b-card-header>
          <b-collapse v-model="showAzurePanel">
            <b-card-body>
              <p class="text-muted small mb-2">
                Le sync vers Chatwoot s'appuie sur les images publiées dans
                <code>{{ azureBaseDesc }}</code>. Ce panneau vérifie que chaque
                image référencée par la proposition est bien présente.
              </p>
              <div v-if="azureImagesLoading" class="text-center my-2">
                <b-spinner small /> Audit en cours…
              </div>
              <div v-else-if="!azureImages.loaded">
                <b-button size="sm" variant="outline-primary" @click="loadAzureImages">
                  <feather-icon icon="RefreshCwIcon" /> Lancer l'audit
                </b-button>
              </div>
              <div v-else-if="!azureImages.azure_configured" class="text-warning">
                Azure docs storage non configuré sur ce serveur
                (<code>DOCS_AZURE_ACCOUNT_NAME</code> /
                <code>DOCS_AZURE_ACCOUNT_KEY</code> manquantes).
              </div>
              <div v-else-if="!azureImages.images.length" class="text-muted">
                Aucune image référencée dans cette proposition.
              </div>
              <div v-else>
                <table class="table table-sm azure-table mb-2">
                  <thead>
                    <tr>
                      <th>Image</th>
                      <th style="width: 80px; text-align: center">Local</th>
                      <th style="width: 80px; text-align: center">Azure</th>
                      <th>URL Azure</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="img in azureImages.images" :key="img.name">
                      <td><code>{{ img.name }}</code></td>
                      <td style="text-align: center">
                        <feather-icon v-if="img.local_exists" icon="CheckIcon" class="text-success" />
                        <feather-icon v-else icon="XIcon" class="text-danger" />
                      </td>
                      <td style="text-align: center">
                        <feather-icon v-if="img.azure_present === true" icon="CheckIcon" class="text-success" />
                        <feather-icon v-else-if="img.azure_present === false" icon="XIcon" class="text-danger" />
                        <feather-icon v-else icon="HelpCircleIcon" class="text-muted" />
                      </td>
                      <td class="text-muted small">
                        <a v-if="img.azure_url && img.azure_present" :href="img.azure_url" target="_blank" rel="noopener">{{ img.azure_url }}</a>
                        <span v-else class="text-muted">{{ img.azure_url || '—' }}</span>
                      </td>
                    </tr>
                  </tbody>
                </table>
                <div class="d-flex" style="gap: 8px">
                  <b-button size="sm" variant="outline-secondary" :disabled="azureImagesLoading" @click="loadAzureImages">
                    <feather-icon icon="RefreshCwIcon" /> Rafraîchir
                  </b-button>
                  <b-button
                    size="sm"
                    variant="primary"
                    :disabled="azureMissingCount === 0 || azureSyncing"
                    @click="syncAzureImages"
                  >
                    <b-spinner v-if="azureSyncing" small />
                    <feather-icon v-else icon="UploadCloudIcon" />
                    Synchroniser les manquantes ({{ azureMissingCount }})
                  </b-button>
                </div>
              </div>
            </b-card-body>
          </b-collapse>
        </b-card>
      </template>
    </b-card>
  </section>
</template>

<script>
import {
  BCard,
  BCardHeader,
  BCardBody,
  BRow,
  BCol,
  BButton,
  BSpinner,
  BBadge,
  BModal,
  BCollapse,
} from 'bootstrap-vue'
import Ripple from 'vue-ripple-directive'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import axios from '@axios'
import MarkdownEditor from '@/components/MarkdownEditor.vue'
import MetadataPanel from '@/components/MetadataPanel.vue'
import PublishedPreview from '@/components/PublishedPreview.vue'
import WysiwygEditor from '@/components/WysiwygEditor.vue'

const DOCS_API_BASE = 'https://api2.buxida.com'

// Liste de fallback si l'endpoint /admin/docs/categories n'est pas dispo
// (deploiement progressif). Doit rester synchro avec ALLOWED_CATEGORIES
// cote api/helpers/buxidaDocs.js.
const FALLBACK_CATEGORIES = [
  'demarrer',
  'reservation-en-ligne',
  'au-quotidien',
  'communication-client',
  'comptabilite-stats',
  'integrations',
  'aide',
]

function emptyMetadata() {
  return { title: '', category: '', related_repos: [], chatwoot_status: 'draft' }
}

const SHOW_PUBLISHED_KEY = 'docs_show_published'
const EDITOR_MODE_KEY = 'docs_editor_mode' // 'markdown' (mavon) | 'wysiwyg' (toast)

function readShowPublished() {
  try {
    const v = window.localStorage.getItem(SHOW_PUBLISHED_KEY)
    if (v === '0') return false
    return true
  } catch (_) {
    return true
  }
}

function writeShowPublished(value) {
  try {
    window.localStorage.setItem(SHOW_PUBLISHED_KEY, value ? '1' : '0')
  } catch (_) { /* noop */ }
}

function readEditorMode() {
  try {
    const v = window.localStorage.getItem(EDITOR_MODE_KEY)
    return v === 'wysiwyg' ? 'wysiwyg' : 'markdown'
  } catch (_) {
    return 'markdown'
  }
}

function writeEditorMode(value) {
  try {
    window.localStorage.setItem(EDITOR_MODE_KEY, value)
  } catch (_) { /* noop */ }
}

function metadataFromFm(fm) {
  let relatedRepos = []
  if (Array.isArray(fm.related_repos)) {
    relatedRepos = fm.related_repos.slice()
  } else if (fm.related_repos) {
    relatedRepos = [fm.related_repos]
  }
  return {
    title: fm.title || '',
    category: fm.category || '',
    related_repos: relatedRepos,
    chatwoot_status: fm.chatwoot_status || 'draft',
  }
}

export default {
  components: {
    BCard,
    BCardHeader,
    BCardBody,
    BRow,
    BCol,
    BButton,
    BSpinner,
    BBadge,
    BModal,
    BCollapse,
    MarkdownEditor,
    MetadataPanel,
    PublishedPreview,
    WysiwygEditor,
  },
  directives: { Ripple },
  data() {
    return {
      loading: true,
      saving: false,
      publishing: false,
      rejecting: false,
      proposalSource: '',
      proposalSummary: '',
      proposalCreatedAt: '',
      isNewArticle: false,
      publishedBody: '',
      body: '',
      bodyOriginal: '',
      metadata: emptyMetadata(),
      metadataOriginal: emptyMetadata(),
      relatedReposSuggestions: [],
      categories: FALLBACK_CATEGORIES.slice(),
      // Toggle "Version publiee" - persiste pour que l'admin retrouve
      // sa preference d'affichage d'une session a l'autre.
      showPublished: readShowPublished(),
      // V2-P4 — Choix de l'editeur : 'markdown' (mavon, defaut) ou
      // 'wysiwyg' (Toast UI). La preference est persistee par admin.
      editorMode: readEditorMode(),
      // V2-P3 — Audit Azure des images
      showAzurePanel: false,
      azureImagesLoading: false,
      azureSyncing: false,
      azureImages: { loaded: false, azure_configured: false, images: [] },
    }
  },
  computed: {
    slug() { return this.$route.params.slug },
    dirty() {
      return this.body !== this.bodyOriginal
        || JSON.stringify(this.metadata) !== JSON.stringify(this.metadataOriginal)
    },
    azureMissingCount() {
      if (!this.azureImages.loaded) return 0
      return this.azureImages.images.filter(
        i => i.local_exists && i.azure_present === false,
      ).length
    },
    azureSummary() {
      const total = this.azureImages.images.length
      const present = this.azureImages.images.filter(i => i.azure_present === true).length
      return `${present}/${total} sur Azure`
    },
    azureBaseDesc() {
      // Hint visuel sur la base Azure. Pas critique d'etre exact ici,
      // c'est juste un repere pour le user.
      return 'ubicx.blob.core.windows.net/bbc/help-buxida/'
    },
  },
  mounted() {
    this.load()
    this.loadSuggestions()
    this.loadCategories()
  },
  methods: {
    async load() {
      this.loading = true
      try {
        const response = await axios.get(`${DOCS_API_BASE}/admin/docs/proposals/${encodeURIComponent(this.slug)}`)
        const data = response.data || {}
        if (data.res !== 1) {
          this.toastError('Chargement', (data && data.detail) || 'Réponse inattendue')
          this.$router.push({ name: 'docs' })
          return
        }
        const p = data.proposal || {}
        const fm = p.front_matter || {}
        this.proposalSource = fm.proposal_source || ''
        this.proposalSummary = fm.proposal_summary || ''
        this.proposalCreatedAt = fm.proposal_created_at || ''
        this.body = p.body || ''
        this.bodyOriginal = p.body || ''
        this.metadata = metadataFromFm(fm)
        this.metadataOriginal = metadataFromFm(fm)
        const published = data.published || null
        this.isNewArticle = !published
        this.publishedBody = published ? published.body : ''
      } catch (err) {
        this.toastError('Chargement', this.formatErr(err))
        this.$router.push({ name: 'docs' })
      } finally {
        this.loading = false
      }
    },
    async loadSuggestions() {
      try {
        const response = await axios.get(`${DOCS_API_BASE}/admin/docs/related-repos/suggestions`)
        const data = response.data || {}
        if (data.res === 1 && Array.isArray(data.items)) {
          this.relatedReposSuggestions = data.items
        }
      } catch (_) {
        // Pas bloquant : l'admin peut taper sa valeur a la main.
      }
    },
    async loadCategories() {
      try {
        const response = await axios.get(`${DOCS_API_BASE}/admin/docs/categories`)
        const data = response.data || {}
        if (data.res === 1 && Array.isArray(data.items) && data.items.length) {
          this.categories = data.items
        }
      } catch (_) {
        // On garde le fallback.
      }
    },
    validateBeforeSave() {
      const t = (this.metadata.title || '').trim()
      if (!t) {
        this.toastError('Sauvegarde', 'Le titre est obligatoire.')
        return false
      }
      if (!this.metadata.category) {
        this.toastError('Sauvegarde', 'La catégorie est obligatoire.')
        return false
      }
      return true
    },
    async save() {
      if (!this.dirty || this.saving) return
      if (!this.validateBeforeSave()) return
      this.saving = true
      try {
        const response = await axios.put(
          `${DOCS_API_BASE}/admin/docs/proposals/${encodeURIComponent(this.slug)}`,
          {
            frontMatter: {
              title: this.metadata.title.trim(),
              category: this.metadata.category,
              related_repos: this.metadata.related_repos,
              chatwoot_status: this.metadata.chatwoot_status || 'draft',
            },
            body: this.body,
          },
        )
        const data = response.data || {}
        if (data.res === 1) {
          this.bodyOriginal = this.body
          this.metadataOriginal = metadataFromFm({
            title: this.metadata.title,
            category: this.metadata.category,
            related_repos: this.metadata.related_repos,
            chatwoot_status: this.metadata.chatwoot_status,
          })
          this.toastSuccess('Sauvegardé', 'Proposition mise à jour côté GitHub.')
        } else {
          this.toastError('Sauvegarde', (data && data.detail) || 'Réponse inattendue')
        }
      } catch (err) {
        this.toastError('Sauvegarde', this.formatErr(err))
      } finally {
        this.saving = false
      }
    },
    async publish() {
      if (this.publishing) return
      if (this.dirty) {
        await this.save()
        if (this.dirty) return
      }
      this.publishing = true
      try {
        const response = await axios.post(
          `${DOCS_API_BASE}/admin/docs/proposals/${encodeURIComponent(this.slug)}/publish`,
        )
        const data = response.data || {}
        if (data.res === 1) {
          const r = data.result || {}
          let title = 'Publié 🎉'
          let icon = 'CheckIcon'
          let variant = 'success'
          let text
          if (r.sync_error) {
            title = 'Publié (avec avertissement)'
            icon = 'AlertTriangleIcon'
            variant = 'warning'
            text = `Article publié dans buxida-docs, mais sync Chatwoot a échoué : ${r.sync_error}`
          } else if (r.sync_deferred) {
            text = 'Article publié dans buxida-docs. La synchronisation vers Chatwoot sera faite au prochain run du script de sync.'
          } else {
            text = `Article publié et synchronisé avec Chatwoot (article_id=${r.article_id || 'n/a'}, ${r.images_uploaded || 0} images).`
          }
          this.$toast({
            component: ToastificationContent,
            props: { title, icon, variant, text },
          }, { timeout: 8000, position: 'top-center' })
          this.$router.push({ name: 'docs' })
        } else {
          this.toastError('Publication', (data && data.detail) || 'Réponse inattendue')
        }
      } catch (err) {
        this.toastError('Publication', this.formatErr(err))
      } finally {
        this.publishing = false
      }
    },
    confirmReject() {
      this.$refs.confirmRejectModal.show()
    },
    async reject() {
      if (this.rejecting) return
      this.rejecting = true
      try {
        const response = await axios.post(
          `${DOCS_API_BASE}/admin/docs/proposals/${encodeURIComponent(this.slug)}/reject`,
        )
        const data = response.data || {}
        if (data.res === 1) {
          this.toastSuccess('Rejetée', 'La proposition a été supprimée.')
          this.$router.push({ name: 'docs' })
        } else {
          this.toastError('Rejet', (data && data.detail) || 'Réponse inattendue')
        }
      } catch (err) {
        this.toastError('Rejet', this.formatErr(err))
      } finally {
        this.rejecting = false
      }
    },
    onLibraryError(message) {
      this.toastError('Bibliothèque d\'images', message || 'Erreur inconnue')
    },
    togglePublished() {
      this.showPublished = !this.showPublished
      writeShowPublished(this.showPublished)
    },
    toggleEditorMode() {
      this.editorMode = this.editorMode === 'wysiwyg' ? 'markdown' : 'wysiwyg'
      writeEditorMode(this.editorMode)
    },
    toggleAzurePanel() {
      this.showAzurePanel = !this.showAzurePanel
      if (this.showAzurePanel && !this.azureImages.loaded && !this.azureImagesLoading) {
        this.loadAzureImages()
      }
    },
    async loadAzureImages() {
      this.azureImagesLoading = true
      try {
        const response = await axios.get(
          `${DOCS_API_BASE}/admin/docs/proposals/${encodeURIComponent(this.slug)}/azure-images`,
        )
        const data = response.data || {}
        if (data.res === 1 && data.result) {
          this.azureImages = {
            loaded: true,
            azure_configured: !!data.result.azure_configured,
            images: Array.isArray(data.result.images) ? data.result.images : [],
          }
        } else {
          this.toastError('Audit Azure', (data && data.detail) || 'Réponse inattendue')
        }
      } catch (err) {
        this.toastError('Audit Azure', this.formatErr(err))
      } finally {
        this.azureImagesLoading = false
      }
    },
    async syncAzureImages() {
      if (this.azureSyncing) return
      this.azureSyncing = true
      try {
        const response = await axios.post(
          `${DOCS_API_BASE}/admin/docs/proposals/${encodeURIComponent(this.slug)}/azure-images/sync`,
          {},
        )
        const data = response.data || {}
        if (data.res === 1) {
          const r = data.result || {}
          const upN = (r.uploaded || []).length
          const skN = (r.skipped || []).length
          const errN = (r.errors || []).length
          let msg = `${upN} image${upN > 1 ? 's' : ''} poussée${upN > 1 ? 's' : ''} sur Azure, ${skN} déjà en place`
          if (errN) msg += `, ⚠️ ${errN} erreur${errN > 1 ? 's' : ''}`
          if (errN) {
            const firstErr = (r.errors[0] && r.errors[0].error) || ''
            this.toastError('Sync Azure', `${msg} — ${firstErr}`)
          } else {
            this.toastSuccess('Sync Azure 🚀', `${msg}.`)
          }
          await this.loadAzureImages()
        } else {
          this.toastError('Sync Azure', (data && data.detail) || 'Réponse inattendue')
        }
      } catch (err) {
        this.toastError('Sync Azure', this.formatErr(err))
      } finally {
        this.azureSyncing = false
      }
    },
    buildDraftPrompt() {
      // Genere un prompt pret a coller dans une session Claude Code web
      // bi-repo (ubicx/buxapi + ubicx/buxida-docs). Cible la redaction
      // d'un article DESTINE AUX GESTIONNAIRES DE CENTRES (cf. CLAUDE.md
      // > Vocabulaire & acteurs : "Gestionnaire de centre").
      const title = this.metadata.title || this.slug
      const category = this.metadata.category || ''
      const categoryLabel = this.categoryLabel(category)
      const repos = Array.isArray(this.metadata.related_repos)
        ? this.metadata.related_repos
        : []

      const reposBlock = repos.length
        ? repos.map(r => `  - \`${r}\``).join('\n')
        : '  (aucun repo lié défini — édite d\'abord les métadonnées de l\'article)'

      return [
        'Rédige le contenu de l\'article '
        + `\`articles/_proposals/${this.slug}.md\` dans le repo `
        + '`ubicx/buxida-docs`.',
        '',
        '## Cible',
        '',
        '**Gestionnaire de centre BUXIDA** (le client de BUXIDA qui gère son '
        + 'centre de self-stockage via `mon.buxida.com/admin`). Style direct, '
        + 'pratique, vouvoiement. Ce n\'est PAS le client final (locataire de box), '
        + 'ni l\'administrateur BUXIDA UBICX.',
        '',
        '## Sujet',
        '',
        `- **Titre** : ${title}`,
        `- **Catégorie BUXIDA** : \`${category}\` (${categoryLabel || 'libellé à définir'})`,
        '',
        '## Sources à consulter dans `ubicx/buxapi`',
        '',
        reposBlock,
        '',
        'Lis aussi `CLAUDE.md` du repo `ubicx/buxapi` pour le contexte global, '
        + 'notamment la section "Vocabulaire & acteurs" et la section produit '
        + 'pertinente pour ce sujet.',
        '',
        '## Consignes de rédaction',
        '',
        '1. **Garde le front-matter YAML intact** (`title`, `slug`, `category`, '
        + '`related_repos`, `chatwoot_*`, `proposal_*`). Ne modifie que le body.',
        '2. **Style** : français, direct, pratique. Vouvoie le lecteur. Pas de '
        + 'blabla marketing, pas de section "Conclusion".',
        '3. **Structure** type :',
        '   - Une phrase d\'introduction qui dit ce que la fonctionnalité permet.',
        '   - Sections H2 pour les principaux cas d\'usage / étapes.',
        '   - Listes à puces pour les actions courtes, paragraphes pour les '
        + 'explications.',
        '4. **Images** : référence uniquement celles déjà présentes dans '
        + '`articles/_images/` du repo. Format : `![texte alternatif](_images/'
        + 'nom-fichier.png)`. Si tu identifies des captures manquantes, laisse '
        + 'un placeholder `<!-- TODO image : description -->` que je remplirai '
        + 'depuis l\'admin avec le bouton "Bibliothèque d\'images".',
        '5. **Précision technique** : si tu cites un nom de bouton, un libellé '
        + 'd\'écran ou un parcours, base-toi sur le code source réel (templates '
        + 'HTML/Vue, controllers, etc.) — pas d\'invention.',
        '6. **Liens internes** : si un autre article documente déjà un concept '
        + 'lié, fais un lien `[texte](slug-de-l-article)` (sans extension, sans '
        + '`articles/`). Le sync Chatwoot rend ces liens cliquables côté Help '
        + 'Center.',
        '',
        '## Livraison',
        '',
        'Une fois rédigé, commit + push sur `main` de `ubicx/buxida-docs` avec '
        + 'le message :',
        '',
        `    docs(proposal): rédige ${this.slug}`,
        '',
        'Je relirai depuis l\'admin BUXIDA (menu Documentation), ajusterai si '
        + 'besoin et publierai.',
      ].join('\n')
    },
    async copyDraftPrompt() {
      const prompt = this.buildDraftPrompt()
      try {
        if (navigator.clipboard && navigator.clipboard.writeText) {
          await navigator.clipboard.writeText(prompt)
        } else {
          // Fallback pour vieux navigateurs : textarea cache + execCommand
          const ta = document.createElement('textarea')
          ta.value = prompt
          ta.style.position = 'fixed'
          ta.style.opacity = '0'
          document.body.appendChild(ta)
          ta.select()
          document.execCommand('copy')
          document.body.removeChild(ta)
        }
        this.toastSuccess('Prompt copié 📋',
          'Colle-le dans une session Claude Code web avec ubicx/buxapi + ubicx/buxida-docs attachés.')
      } catch (e) {
        this.toastError('Copie impossible', (e && e.message) || 'Le navigateur a refusé l\'accès au presse-papiers.')
      }
    },
    categoryLabel(slug) {
      // Mirroring du mapping cote sync-docs-to-chatwoot.js + MetadataPanel.
      const labels = {
        demarrer: 'Démarrer',
        'reservation-en-ligne': 'Réservation en ligne',
        'au-quotidien': 'Au quotidien',
        'communication-client': 'Communication client',
        'comptabilite-stats': 'Comptabilité & statistiques',
        integrations: 'Intégrations',
        aide: 'Aide',
      }
      return labels[slug] || ''
    },
    formatDate(iso) {
      if (!iso) return ''
      try {
        const d = new Date(iso)
        return d.toLocaleString('fr-FR', { year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' })
      } catch (_) { return iso }
    },
    formatErr(err) {
      return (err && err.response && err.response.data && err.response.data.detail)
        || (err && err.message)
        || 'Erreur inconnue'
    },
    toastError(title, text) {
      this.$toast({
        component: ToastificationContent,
        props: { title, icon: 'AlertTriangleIcon', variant: 'danger', text },
      }, { timeout: 8000, position: 'top-center' })
    },
    toastSuccess(title, text) {
      this.$toast({
        component: ToastificationContent,
        props: { title, icon: 'CheckIcon', variant: 'success', text },
      }, { timeout: 4000, position: 'top-center' })
    },
  },
}
</script>

<style scoped>
.azure-panel {
  border: 1px solid #e3e3e3;
}
.azure-table {
  font-size: 13px;
}
.azure-table td,
.azure-table th {
  padding: 4px 8px;
  vertical-align: middle;
}
.azure-table code {
  font-size: 0.9em;
}
</style>
